import { aptGenerationApi } from '@/api/apt_generation.api';
import { modelsApi } from '@/api/models.api';
import { projectsApi } from '@/api/projects.api';
import { ActionButton } from '@/components/common/ActionButton';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { PageHeader } from '@/components/common/PageHeader';
import { Reveal } from '@/components/common/Reveal';
import { PanelSkeleton, SkeletonBlock, TableSkeleton } from '@/components/common/Skeleton';
import { CARD_PADDED, ICON_TILE, PANEL_TITLE } from '@/components/common/surfaces';
import { ConfirmDeleteDialog } from '@/components/dialogs/ConfirmDeleteDialog';
import { APTGenerationPanel } from '@/components/project-detail/APTGenerationPanel';
import { CollisionDetectionPanel } from '@/components/project-detail/CollisionDetectionPanel';
import { DetectionsPanel } from '@/components/project-detail/DetectionsPanel';
import { ImportWarningsPanel } from '@/components/project-detail/ImportWarningsPanel';
import { MaterialsPanel } from '@/components/project-detail/MaterialsPanel';
import { ModelColumn } from '@/components/project-detail/ModelColumn';
import { ModelUploadZone } from '@/components/project-detail/ModelUploadZone';
import { NCDocumentPanel } from '@/components/project-detail/NCDocumentPanel';
import { PocketsPanel } from '@/components/project-detail/PocketsPanel';
import { RuleSetPanel } from '@/components/project-detail/RuleSetPanel';
import { ProjectTitleEditor } from '@/components/project-detail/ProjectTitleEditor';
import { ProjectUploadingOverlay } from '@/components/project-detail/ProjectUploadingOverlay';
import { clearProjectTask, projectTaskStorageKey, useUploadQueueContext } from '@/context/UploadQueueContext';
import { useProjectDetail } from '@/hooks/useProjectDetail';
import { useProjects } from '@/hooks/useProjects';
import { downloadBlob } from '@/utils/download';
import { Cube, DownloadSimple } from '@phosphor-icons/react';
import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

export const ProjectDetail: FC = () => {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const { project, isLoading, error, updateProject, refetch } = useProjectDetail(projectId!);

  // Project list hook still provides delete + refetch used by this page; the
  // model preview URLs/IDs now come from the consolidated overview payload.
  const { deleteProject, refetch: refetchProjects } = useProjects();

  const { getUploadForProject } = useUploadQueueContext();
  const activeUpload = projectId ? getUploadForProject(projectId) : undefined;

  const storedTaskFromLs =
    typeof window !== 'undefined' && projectId ? localStorage.getItem(projectTaskStorageKey(projectId)) : null;

  const [serverUploadTaskId, setServerUploadTaskId] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const { task_id } = await modelsApi.getProjectModelUploadTask(projectId);
        if (!cancelled) {
          setServerUploadTaskId(task_id ?? null);
        }
      } catch {
        if (!cancelled) {
          setServerUploadTaskId(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const isUploadingFromQueue = Boolean(
    activeUpload && (activeUpload.status === 'uploading' || activeUpload.status === 'processing')
  );
  const isProcessingUpload =
    isUploadingFromQueue || Boolean(!activeUpload && storedTaskFromLs) || Boolean(serverUploadTaskId);

  const uploadOverlayTaskId = activeUpload?.taskId ?? storedTaskFromLs ?? serverUploadTaskId;
  const subscribeUploadOverlayToSse = !activeUpload && Boolean(storedTaskFromLs || serverUploadTaskId);

  const handleUploadOverlayComplete = useCallback(() => {
    if (projectId) {
      clearProjectTask(projectId);
    }
    setServerUploadTaskId(null);
    void refetch();
    void refetchProjects();
  }, [projectId, refetch, refetchProjects]);

  const prevProcessingUploadRef = useRef(false);
  useEffect(() => {
    if (projectId) {
      prevProcessingUploadRef.current = false;
    }
  }, [projectId]);

  useEffect(() => {
    if (prevProcessingUploadRef.current && !isProcessingUpload) {
      void refetch();
      void refetchProjects();
    }
    prevProcessingUploadRef.current = isProcessingUpload;
  }, [isProcessingUpload, refetch, refetchProjects]);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [_isDeleting, setIsDeleting] = useState(false);
  const [ncDocumentRefreshKey, setNcDocumentRefreshKey] = useState(0);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [aptGenerationReady, setAptGenerationReady] = useState(false);
  const [isDownloadingHFiles, setIsDownloadingHFiles] = useState(false);
  const [isDownloadingEmbedded, setIsDownloadingEmbedded] = useState(false);
  const [isSubmittingModel, setIsSubmittingModel] = useState(false);

  /**
   * Upload the one STEP that carries both the model and its stock.
   *
   * Handing the returned task id to `serverUploadTaskId` flips the page into the same
   * full-page progress overlay that a project-creation upload uses, so this path needs no
   * progress plumbing of its own — the overlay refetches the project when the task completes.
   */
  const handleUploadModel = async (file: File) => {
    if (!projectId) return;
    setIsSubmittingModel(true);
    try {
      const { uid: taskId } = await modelsApi.uploadEmbeddedBackground(projectId, file);
      setServerUploadTaskId(taskId);
    } catch (error) {
      console.error('Failed to start the STEP upload:', error);
      toast.error(t('models.failedToUpload'));
    } finally {
      setIsSubmittingModel(false);
    }
  };

  const handleDownloadEmbedded = async () => {
    if (!projectId) return;
    setIsDownloadingEmbedded(true);
    try {
      const blob = await projectsApi.downloadEmbeddedSTP(projectId);
      const embeddedFilename = `${(project?.title || projectId).replace(/ /g, '_')}-embedded.stp`;
      downloadBlob(blob, embeddedFilename);
      toast.success(t('models.embeddedDownloaded'));
    } catch (_e) {
      toast.error(t('models.embeddedDownloadFailed'));
    } finally {
      setIsDownloadingEmbedded(false);
    }
  };

  const handleDownloadProjectHFiles = async () => {
    if (!projectId) return;
    setIsDownloadingHFiles(true);
    try {
      const response = await aptGenerationApi.downloadProjectHFilesZip(projectId);
      let filename = `h_files_project_${projectId}.zip`;
      const contentDisposition = (response.headers as Record<string, string>)?.['content-disposition'];
      if (contentDisposition) {
        const match = /filename="?([^";\n]+)"?/.exec(contentDisposition);
        if (match?.[1]) filename = match[1];
      }
      const url = URL.createObjectURL(new Blob([response.data as BlobPart]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('projectDetail.downloadError') || 'Failed to download H files');
    } finally {
      setIsDownloadingHFiles(false);
    }
  };

  // Poll project detail while collision simulation is running
  const collisionState = project?.collision_simulation_state ?? null;

  useEffect(() => {
    if (collisionState !== 'running') return;
    const intervalMs = 8000;
    const id = setInterval(() => {
      refetch();
    }, intervalMs);
    return () => clearInterval(id);
  }, [collisionState, refetch]);

  // The project has one STEP document; the model and the stock are two solids of it, previewed
  // from the same id. `model_id` is null until a STEP has been uploaded.
  const modelId = project?.model_id ?? null;

  const handleDeleteProject = async () => {
    setIsDeleting(true);
    try {
      await deleteProject(projectId!);
      navigate('/');
    } catch (_error) {
      // Error is handled by hook
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  if (isProcessingUpload) {
    return (
      <ProjectUploadingOverlay
        taskId={uploadOverlayTaskId}
        progressFromContext={activeUpload?.progress ?? 0}
        subscribeToTaskStream={subscribeUploadOverlayToSse}
        onComplete={handleUploadOverlayComplete}
        onStreamError={() => {
          if (projectId) {
            clearProjectTask(projectId);
          }
          setServerUploadTaskId(null);
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <SkeletonBlock className="h-4 w-32" />
          <SkeletonBlock className="h-9 w-80 max-w-full" />
        </div>
        <div className={CARD_PADDED}>
          <PanelSkeleton lines={2} />
        </div>
        <div className={CARD_PADDED}>
          <PanelSkeleton lines={6} />
        </div>
        <TableSkeleton rows={6} columns={4} />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={t('projectDetail.title')} backTo={{ to: '/', label: t('projectDetail.backToProjects') }} />
        <ErrorMessage message={error || t('projectDetail.failedToLoad')} />
      </div>
    );
  }

  if (project.apt_generation_error) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={project.title} backTo={{ to: '/', label: t('projectDetail.backToProjects') }} />
        <ErrorMessage message={project.apt_generation_error} />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-6">
        <Reveal>
          <PageHeader
            title={project.title}
            titleSlot={<ProjectTitleEditor title={project.title} onSave={updateProject} />}
            backTo={{ to: '/', label: t('projectDetail.backToProjects') }}
            actions={
              <ActionButton
                icon={<DownloadSimple size={16} aria-hidden="true" />}
                onClick={handleDownloadProjectHFiles}
                loading={isDownloadingHFiles}
                title={t('projectDetail.downloadProjectHFilesTooltip')}
              >
                {t('projectDetail.downloadProjectHFiles')}
              </ActionButton>
            }
          />
        </Reveal>

        {/* Non-fatal STEP import warnings */}
        <ImportWarningsPanel modelId={modelId} />

        <Reveal delayMs={80}>
          <CollisionDetectionPanel project={project} />
        </Reveal>

        {/* NC Document: load only after all dependent sections are ready (APT Generation loaded
            including holes, no regeneration in progress) */}
        <Reveal delayMs={120}>
          <NCDocumentPanel
            projectId={projectId!}
            projectTitle={project.title}
            refreshKey={ncDocumentRefreshKey}
            canLoad={aptGenerationReady && !isRegenerating}
            blurred={!aptGenerationReady || isRegenerating}
          />
        </Reveal>

        {/* The model and its stock: two previews of the one uploaded STEP document */}
        <Reveal delayMs={160}>
          <div className={CARD_PADDED}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex min-w-0 items-center gap-3">
                <span className={ICON_TILE}>
                  <Cube size={16} aria-hidden="true" />
                </span>
                <h2 className={PANEL_TITLE}>{t('models.modelSection')}</h2>
              </div>
              {modelId && (
                <ActionButton
                  icon={<DownloadSimple size={16} aria-hidden="true" />}
                  onClick={handleDownloadEmbedded}
                  loading={isDownloadingEmbedded}
                >
                  {t('models.downloadEmbedded')}
                </ActionButton>
              )}
            </div>

            <div className="mt-6">
              {modelId ? (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <ModelColumn
                    title={t('models.model')}
                    modelId={modelId}
                    solid="model"
                    hasPreview={Boolean(project.model_png_url)}
                  />

                  <ModelColumn
                    title={t('models.stock')}
                    modelId={modelId}
                    solid="stock"
                    hasPreview={Boolean(project.stock_png_url)}
                  />
                </div>
              ) : (
                <ModelUploadZone onUpload={handleUploadModel} isUploading={isSubmittingModel} />
              )}
            </div>
          </div>
        </Reveal>

        {/* Material of the STEP document, as the file names it and as the catalog resolved it */}
        <Reveal delayMs={200}>
          <MaterialsPanel modelId={modelId} />
        </Reveal>

        {/* Hole rule set the project's drilling and milling sequence is generated from */}
        <Reveal delayMs={240}>
          <RuleSetPanel
            projectId={projectId!}
            holeRuleSet={project.hole_rule_set ?? null}
            toolSystem={project.tool_system}
            isFixed={Boolean(modelId)}
            onSaved={refetch}
          />
        </Reveal>

        {/* Detections of the one STEP document */}
        <Reveal delayMs={280}>
          <DetectionsPanel
            projectId={projectId!}
            modelId={modelId}
            title={t('detections.title')}
            onRegenerationComplete={() => setNcDocumentRefreshKey((k) => k + 1)}
            onRegeneratingChange={setIsRegenerating}
          />
        </Reveal>

        {/* APT Generation */}
        <APTGenerationPanel projectId={projectId!} modelId={modelId} onAptGenerationReady={setAptGenerationReady} />

        <PocketsPanel projectId={projectId!} modelId={modelId} />

        {/* The one destructive action on the page, kept away from everything it could be mistaken
            for by sitting alone under the last panel. */}
        <div className="flex justify-end">
          <ActionButton variant="danger" onClick={() => setShowDeleteDialog(true)}>
            {t('projectDetail.deleteProject')}
          </ActionButton>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <ConfirmDeleteDialog
        visible={showDeleteDialog}
        title={t('projectDetail.deleteProjectTitle')}
        message={t('projectDetail.deleteProjectConfirm')}
        onConfirm={handleDeleteProject}
        onCancel={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
